import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type TwoFAStatus = {
  enabled: boolean;
  enabled_at: string | null;
  backup_codes_remaining: number;
};

export type TwoFASetup = {
  secret: string;
  provisioning_uri: string;
  qr_data_url: string;
};

export type TwoFAConfirm = {
  enabled_at: string;
  backup_codes: string[];
};

export function useTwoFAStatus() {
  return useQuery<TwoFAStatus>({
    queryKey: ["2fa-status"],
    queryFn: async () => (await api.get<TwoFAStatus>("/athlete/2fa/status")).data,
    staleTime: 30_000,
  });
}

export function useTwoFASetup() {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<TwoFASetup>("/athlete/2fa/setup");
      return r.data;
    },
  });
}

export function useTwoFAConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const r = await api.post<TwoFAConfirm>("/athlete/2fa/confirm", { code });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["2fa-status"] }),
  });
}

export function useTwoFADisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { code?: string; backup_code?: string }) => {
      await api.post("/athlete/2fa/disable", vars);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["2fa-status"] }),
  });
}

export function useTwoFARegenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const r = await api.post<{ backup_codes: string[] }>(
        "/athlete/2fa/regenerate-backup-codes",
        { code },
      );
      return r.data.backup_codes;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["2fa-status"] }),
  });
}

export type TwoFAChallengeError = {
  reason: "2fa_required";
  tmp_token: string;
};

export function useTwoFAChallenge() {
  return useMutation({
    mutationFn: async (vars: { tmp_token: string; code?: string; backup_code?: string }) => {
      const r = await api.post("/athlete/auth/2fa-challenge", vars);
      return r.data;
    },
  });
}

export function useTwoFARecoveryRequest() {
  return useMutation({
    mutationFn: async (email: string) => {
      await api.post("/athlete/2fa/recovery-request", { email });
    },
  });
}
